import { useState, useRef, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { encode, decode } from '../utils/encryptionUtils';

const createMutex = () => {
  let lock = Promise.resolve();

  return function queue(fn) {
    const run = () => fn().catch(console.error);
    const current = lock;
    lock = lock.then(() => run());
    return current;
  };
};

// Shared mutex across hook instances
const updateMutex = createMutex();

const useEncryptedURLState = (key, defaultValue, options = {}) => {
  const { encrypt = true } = options;
  const history = useHistory();
  const location = useLocation();

  const parseFromSearch = (search) => {
    const searchParams = new URLSearchParams(search);
    const raw = searchParams.get(key);

    if (!raw) return typeof defaultValue === 'function' ? defaultValue() : defaultValue;

    if (!encrypt) {
      try {
        // Try to parse JSON so numbers/booleans/null round-trip correctly
        return JSON.parse(raw);
      } catch (e) {
        // Fallback to plain string for legacy/non-JSON values
        return raw;
      }
    }

    try {
      const decrypted = decode(raw);
      return JSON.parse(decrypted);
    } catch (e) {
      // If value isn't encrypted/JSON, treat it as a plain string
      return raw;
    }
  };

  const [state, setState] = useState(() => parseFromSearch(location.search));

  const stateRef = useRef(state);
  stateRef.current = state;

  // Track last search string written by this hook instance so we can distinguish
  // our own history.replace calls from external navigation (browser back/forward).
  const lastWrittenSearchRef = useRef(location.search);

  const updateState = (newValue) => {
    updateMutex(async () => {
      const resolvedValue = typeof newValue === 'function' ? newValue(stateRef.current) : newValue;
      stateRef.current = resolvedValue;
      setState(resolvedValue);

      const currentParams = new URLSearchParams(history.location.search);
      if (encrypt) {
        const encryptedValue = encode(JSON.stringify(resolvedValue));
        currentParams.set(key, encryptedValue);
      } else {
        // Serialize to JSON to preserve type information in URL
        const serializedValue = JSON.stringify(resolvedValue);
        currentParams.set(key, serializedValue);
      }

      const newSearch = currentParams.toString();
      lastWrittenSearchRef.current = newSearch;

      history.replace({
        // Use history.location.pathname to avoid stale closure
        pathname: history.location.pathname,
        search: newSearch,
      });
    });
  };

  // Sync state from URL when browser back/forward (POP) navigation changes the location.
  // history.replace / history.push triggered by our own code produces 'REPLACE'/'PUSH'
  // actions, so we only re-read the URL for genuine POP navigations.
  useEffect(() => {
    if (history.action !== 'POP') return;
    const parsed = parseFromSearch(location.search);
    stateRef.current = parsed;
    setState(parsed);
    lastWrittenSearchRef.current = location.search;
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  return [state, updateState];
};

export default useEncryptedURLState;
